// US-BUCKET-05 -- Deactivate a guardian-managed bucket.
// US-BUCKET-14 -- Child deactivates their own child-managed bucket.
//
// Two paths:
//   - zero balance -> one-click confirm, percentage returns to flex pool
//   - non-zero balance -> must pick a target bucket to receive the balance
//
// Target eligibility by actor:
//   - Guardian: only guardian-managed sibling buckets (the guardian is
//     approver-of-record; routing into a child-managed bucket blurs
//     attribution -- enforced by the DB layer too).
//   - Child:    any sibling bucket the child owns, guardian-managed OR
//     child-managed. The DB layer (actor_is_child_owner=true) permits
//     this; here we simply show the full list.

import {
  type AppError,
  type BucketDeactivateOutput,
  type BucketEntry,
  bucketDeactivate,
} from '@chorepop/core/client';
import { type Component, For, Show, createMemo, createSignal } from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';

type Props = {
  sessionId: string;
  bucket: BucketEntry;
  childDisplayName: string;
  /** All active buckets for this child including the one being deactivated. */
  siblings: BucketEntry[];
  currencyCode: string;
  /** True when the signed-in actor is the child who owns the bucket. */
  actorIsChild: boolean;
  onDeactivated: (result: BucketDeactivateOutput) => void;
  onCancel: () => void;
};

export const DeactivateBucketScreen: Component<Props> = (props) => {
  const [targetId, setTargetId] = createSignal<number | null>(null);
  const [submitting, setSubmitting] = createSignal(false);
  const [errorMessage, setErrorMessage] = createSignal<string | null>(null);

  const hasBalance = () => props.bucket.balanceMinorUnits > 0;

  const candidates = createMemo(() =>
    props.siblings.filter((b) => {
      if (b.bucketId === props.bucket.bucketId) return false;
      // Children can move their balance into any of their own buckets
      // (guardian-managed or child-managed). Guardians may only target
      // guardian-managed buckets.
      return props.actorIsChild ? true : b.isGuardianManaged;
    }),
  );

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    setErrorMessage(null);

    if (hasBalance() && targetId() === null) {
      setErrorMessage('Pick a bucket to receive the balance.');
      return;
    }

    setSubmitting(true);
    try {
      const result = await bucketDeactivate({
        sessionId: props.sessionId,
        bucketId: props.bucket.bucketId,
        targetBucketId: hasBalance() ? (targetId() ?? null) : null,
      });
      if (!result.ok) {
        setErrorMessage(humanReadableError(result.error));
        return;
      }
      props.onDeactivated(result.value);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ActionScreen
      class="action-screen--bucket-decision"
      eyebrow={`${props.childDisplayName}'s bucket`}
      title={`Deactivate ${props.bucket.bucketName}`}
      onBack={props.onCancel}
    >
      <p class="action-screen-intro">
        For {props.childDisplayName}. Deactivated buckets disappear from future deposits but stay
        visible in history.
      </p>

      <form onSubmit={handleSubmit} novalidate data-testid="deactivate-bucket-form">
        <Show
          when={hasBalance()}
          fallback={
            <p class="pin-remove-status">
              This bucket has a zero balance. Its {(props.bucket.allocationBps / 100).toFixed(0)}%
              will return to the flexible pool.
            </p>
          }
        >
          <p class="pin-remove-status">
            This bucket holds {formatMinor(props.bucket.balanceMinorUnits, props.currencyCode)}.
            Pick another bucket to receive it.
          </p>

          <Show
            when={candidates().length > 0}
            fallback={
              <div class="form-error" role="alert">
                {props.actorIsChild
                  ? 'There are no other buckets to receive this balance. Add a new bucket first, then try again.'
                  : 'There are no other guardian-managed buckets to receive this balance. Add one first, then try again.'}
              </div>
            }
          >
            <fieldset class="field" style="border: none; padding: 0; margin: 0;">
              <legend class="field-label">Move balance to</legend>
              <For each={candidates()}>
                {(b) => (
                  <label style="display: flex; align-items: center; gap: 8px; padding: 6px 0;">
                    <input
                      type="radio"
                      name="target-bucket"
                      value={b.bucketId}
                      checked={targetId() === b.bucketId}
                      onChange={() => {
                        setTargetId(b.bucketId);
                        setErrorMessage(null);
                      }}
                      disabled={submitting()}
                      data-testid={`deactivate-target-${b.bucketId}`}
                    />
                    <span>
                      {b.bucketName} -- current balance{' '}
                      {formatMinor(b.balanceMinorUnits, props.currencyCode)}
                    </span>
                  </label>
                )}
              </For>
            </fieldset>
          </Show>
        </Show>

        <Show when={errorMessage()}>
          {(m) => (
            <div class="form-error" role="alert">
              {m()}
            </div>
          )}
        </Show>

        <div style="display: flex; gap: 12px; margin-top: 16px;">
          <button
            type="submit"
            class="primary pin-remove-primary"
            disabled={
              submitting() || (hasBalance() && (targetId() === null || candidates().length === 0))
            }
            data-testid="deactivate-bucket-submit"
          >
            {submitting() ? 'Deactivating…' : 'Deactivate'}
          </button>
          <button
            type="button"
            class="secondary"
            onClick={() => props.onCancel()}
            disabled={submitting()}
          >
            Cancel
          </button>
        </div>
      </form>
    </ActionScreen>
  );
};

function formatMinor(minorUnits: number, currencyCode: string): string {
  // Mirror formatBalance from HomeScreen so the copy matches.
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode,
    }).format(minorUnits / 100);
  } catch {
    return `${(minorUnits / 100).toFixed(2)} ${currencyCode}`;
  }
}

function humanReadableError(error: AppError): string {
  switch (error.kind) {
    case 'validation':
    case 'not_found':
    case 'conflict':
      return error.message;
    case 'unauthorized':
      return 'Your session has expired. Please sign in again.';
    case 'network_required':
      return error.message;
    default:
      return `Something went wrong: ${error.message}`;
  }
}
